import { Alert, Button } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRunZmSync, useZmSyncStatus } from '../hooks/useZmSync';

export const zmWhen = (iso?: string | null) => (iso ? format(parseISO(iso), 'dd.MM.yyyy HH:mm') : '–');

export function ZmSyncBanner() {
  const { user } = useAuth();
  const isAdmin = user?.role?.toLowerCase().trim() === 'admin';
  const queryClient = useQueryClient();
  const { data } = useZmSyncStatus();
  const { run, running: rerunning } = useRunZmSync();

  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && data && !data.running) {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['monthlyStatus'] });
      queryClient.invalidateQueries({ queryKey: ['zmSync'] });
    }
    wasRunning.current = !!data?.running;
  }, [data, queryClient]);

  if (!data) return null;

  if (data.running || rerunning) {
    return (
      <Alert color="yellow" mb="sm">
        ZM-Synchronisierung läuft – bitte die App gerade nicht verwenden, Änderungen könnten überschrieben werden.
      </Alert>
    );
  }

  const last = data.last;
  if (!isAdmin || !last || (last.status !== 'failed' && last.status !== 'skipped')) return null;

  const button = (
    <Button size="xs" mt="xs" onClick={run} loading={rerunning}>
      Erneut ausführen
    </Button>
  );

  if (last.status === 'skipped') {
    return (
      <Alert color="orange" mb="sm">
        <strong>
          ZM-Synchronisierung übersprungen am {zmWhen(last.finished_at)}: Die ZM-Tabelle scheint leer zu sein.
        </strong>{' '}
        Es wurden keine Urlaubs-/Abwesenheitsbuchungen (Mo–Fr) gefunden, daher wurden keine Daten geändert. Bitte die
        verantwortliche Person für die ZM-Tabelle kontaktieren. Bis dahin gelten die Daten des letzten erfolgreichen
        Syncs vom {zmWhen(data.last_success_at)}.
        <div>{button}</div>
      </Alert>
    );
  }

  return (
    <Alert color="red" mb="sm">
      ZM-Synchronisierung fehlgeschlagen am {zmWhen(last.finished_at)}: {last.message ?? 'Unbekannter Fehler'}
      <div>{button}</div>
    </Alert>
  );
}
